import PropTypes from 'prop-types';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from '@phosphor-icons/react/dist/ssr/X';
import { SHADOWS } from '../../../styles/UISystem';
import { CROWN_WHITE } from '../../../styles/brandAssets';
import { firstNameOf } from '../../../utils/contactUtils';

/**
 * WelcomeInviteBanner
 *
 * The Anon's invite CTA, docked above the chat input (slides up from the
 * bottom). BLACK card: white crown · "Invite from {firstName}" · "Skip the
 * waitlist" · *Join Royale*. Whole card taps the primary action; a small text X
 * (top-right) permanently dismisses. Non-modal, bottom-docked sibling of the
 * top-anchored ProximityCard. See ui-design-system.md § 4.10.
 *
 * The light "welcome closer" variants ('welcome'/'signedup') were retired with
 * the pull-only ruling (b/exchange-social-track): the tappable "Join Royale to
 * keep this chat" composer line is the self-serve ask now — only a real invite
 * from the Profile raises a card.
 */
const SPRING = { type: 'spring', damping: 26, stiffness: 140, mass: 0.8 };

const cardBase = {
  position: 'relative',
  width: '100%',
  maxWidth: 300,
  minHeight: 108,
  borderRadius: 20,
  boxShadow: SHADOWS.TIER2,
  padding: '14px 20px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  gap: 2,
};

const DismissX = ({ dark, onDismiss }) => (
  <button
    onClick={(e) => {
      e.stopPropagation();
      onDismiss();
    }}
    aria-label="Dismiss"
    style={{
      position: 'absolute',
      top: 6,
      right: 8,
      background: 'none',
      border: 'none',
      padding: 6,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
    }}
  >
    <X size={15} weight="bold" color={dark ? 'rgba(255,255,255,0.5)' : '#C7C7CC'} />
  </button>
);

DismissX.propTypes = { dark: PropTypes.bool, onDismiss: PropTypes.func };

const InviteCard = ({ inviterName, onJoin, onDismiss }) => (
  <motion.div
    onClick={onJoin}
    whileTap={{ scale: 0.99 }}
    transition={{ duration: 0.1 }}
    style={{ ...cardBase, cursor: 'pointer', backgroundColor: '#000000' }}
  >
    <DismissX dark onDismiss={onDismiss} />
    <img
      src={CROWN_WHITE}
      alt=""
      style={{ width: 30, height: 20, objectFit: 'contain', marginBottom: 2 }}
    />
    <span style={{ fontSize: 16, fontWeight: 600, color: '#FFFFFF' }}>
      {inviterName ? `Invite from ${firstNameOf(inviterName)}` : "You're invited"}
    </span>
    <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', lineHeight: 1.3 }}>
      Skip the waitlist
    </span>
    <span
      style={{
        marginTop: 6,
        fontSize: 15,
        fontStyle: 'italic',
        fontWeight: 400,
        color: '#FFFFFF',
      }}
    >
      Join Royale
    </span>
  </motion.div>
);

InviteCard.propTypes = {
  inviterName: PropTypes.string,
  onJoin: PropTypes.func,
  onDismiss: PropTypes.func,
};

export const WelcomeInviteBanner = ({ variant = null, inviterName = null, onJoin, onDismiss }) => (
  <AnimatePresence>
    {variant === 'invite' && (
      <motion.div
        key="invite"
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 80, opacity: 0 }}
        transition={SPRING}
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          marginBottom: 14,
          pointerEvents: 'auto',
        }}
      >
        <InviteCard inviterName={inviterName} onJoin={onJoin} onDismiss={onDismiss} />
      </motion.div>
    )}
  </AnimatePresence>
);

WelcomeInviteBanner.propTypes = {
  /** 'invite' | null (hidden) */
  variant: PropTypes.oneOf(['invite']),
  /** Inviting Profile's name — first name is shown ("Invite from Bri") */
  inviterName: PropTypes.string,
  /** Invite CTA — PWA conversion / waitlist */
  onJoin: PropTypes.func,
  /** Small text X — permanent close */
  onDismiss: PropTypes.func,
};

export default WelcomeInviteBanner;
